import { css } from '@emotion/react'
import { PRE_OPEN_CURRICULUM_KEYS } from '@mathflat/domain/@entities/Curriculum/constant'
import type { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import { SCHOOL_TYPE } from '@mathflat/domain/@entities/SchoolSystem/constants'
import type { ValueOf } from '@mathflat/shared/@types/utilityTypes'
import { computed } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { isMobileOnly } from 'react-device-detect'

import { tJosa } from '~/@app/i18n'
import { DROPDOWN_ITEM_HEIGHT } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useOrientation } from '~/@common/hooks/useOrientation'
import { useRepository } from '~/@common/hooks/useRepository'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import { DropdownItem } from '~/@common/ui/Dropdown'
import Dropdown from '~/@common/ui/Dropdown/Dropdown'
import { Chip } from '~/@common/ui/radio/Chip'
import { Switch } from '~/@common/ui/Switch/Switch'

import {
  LECTURE_STATUS_FILTER,
  LECTURE_STATUS_FILTER_LABEL,
  LectureService,
  type LectureStatusFilter,
} from '../@service/Lecture.service'

type LectureGroupHeaderProps = {
  isShowAllLectures: boolean
  onToggleShowAllLectures: () => void
  lectureStatusFilter: LectureStatusFilter
  onChangeLectureStatusFilter: (filter: LectureStatusFilter) => void
}

const LECTURE_STATUS_FILTER_LIST = Object.values(LECTURE_STATUS_FILTER)

const LectureGroupHeader = ({
  isShowAllLectures,
  onToggleShowAllLectures,
  lectureStatusFilter,
  onChangeLectureStatusFilter,
}: LectureGroupHeaderProps) => {
  const service = useRepository(LectureService)
  const { isMobile } = useStudentAppMediaQuery()
  const { type: orientationType } = useOrientation()
  const isAllLecturesEnabled = commonRepo.permission.byFeature.isAllowed('LECTURE')

  const _store = useLocalObservable(() => ({
    selectedSchoolType: SCHOOL_TYPE.중학교 as ValueOf<typeof SCHOOL_TYPE>,
    selectedCurriculumTrieKey: commonRepo.curriculumCore?.getGradeSemesterMiddle({
      includeAll: false,
    })[0].trieKey,

    onChangeCurriculumDropdown: (trieKey: CurriculumEntity.TrieKey) => {
      if (!commonRepo.studentId) throw new Error('studentId is null')

      const curriculumInfo = commonRepo.curriculumCore?.getItemByTrieKey(trieKey)

      if (!curriculumInfo) throw Error('커리큘럼 정보를 찾을 수 없습니다.')

      service.setCurriculumAndFetchLectureGroups({
        curriculumInfo,
        studentId: commonRepo.studentId,
      })
    },
  }))

  const dropdownList = computed(() => {
    if (!service.selectedCurriculumInfo) return null

    const selectableCurriculumList = isMobile
      ? service.flatAndSelectableCurriculumList
      : service.selectableCurriculumList[service.selectedCurriculumInfo.schoolType]

    return selectableCurriculumList?.filter(
      (curriculum) => curriculum && !PRE_OPEN_CURRICULUM_KEYS.includes(curriculum.trieKey),
    )
  }).get()

  const isMobileLandscape = isMobileOnly && orientationType === 'landscape'

  if (!service.selectedCurriculumInfo) {
    return <></>
  }

  const leftContent = (() => {
    if (isShowAllLectures) {
      return (
        <div className="curriculum-list">
          {!isMobile && (
            <div className="school-type-group">
              <Chip.Group
                onChange={(v) => {
                  service.setSelectSchoolType(v as ValueOf<typeof SCHOOL_TYPE>)
                }}
                value={service.selectedCurriculumInfo.schoolType}
              >
                {/* SOLO 미구독 학교급은 비활성 (선생님앱과 동일 정책) */}
                <Chip.GroupItem
                  value={SCHOOL_TYPE.초등학교}
                  disabled={
                    !commonRepo.permission.bySubscription.isSubscribed({ schoolType: 'ELEMENTARY' })
                  }
                >
                  초
                </Chip.GroupItem>
                <Chip.GroupItem
                  value={SCHOOL_TYPE.중학교}
                  disabled={
                    !commonRepo.permission.bySubscription.isSubscribed({ schoolType: 'MIDDLE' })
                  }
                >
                  중
                </Chip.GroupItem>
                <Chip.GroupItem
                  value={SCHOOL_TYPE.고등학교}
                  disabled={
                    !commonRepo.permission.bySubscription.isSubscribed({ schoolType: 'HIGH' })
                  }
                >
                  고
                </Chip.GroupItem>
              </Chip.Group>
            </div>
          )}
          <Dropdown
            width={160}
            listHeight={isMobileLandscape ? DROPDOWN_ITEM_HEIGHT * 3 : undefined}
            value={service.selectedCurriculumInfo.trieKey}
            onChange={(curriculumKey) => _store.onChangeCurriculumDropdown(curriculumKey)}
          >
            {dropdownList?.map((schoolCurriculum) => {
              // SOLO 미구독 커리큘럼은 disabled (trieKey 기반 — 고등 과목 단위까지 정확)
              const isSubscribedCurriculum = schoolCurriculum
                ? commonRepo.permission.bySubscription.isSubscribed({
                    trieKey: schoolCurriculum.trieKey,
                  })
                : true
              return (
                <DropdownItem
                  key={schoolCurriculum?.trieKey}
                  value={schoolCurriculum?.trieKey}
                  disabled={!isSubscribedCurriculum}
                >
                  {schoolCurriculum?.label}
                </DropdownItem>
              )
            })}
          </Dropdown>
        </div>
      )
    }

    if (isMobile) {
      return (
        <Dropdown
          width={160}
          value={lectureStatusFilter}
          onChange={(value) => onChangeLectureStatusFilter(value as LectureStatusFilter)}
        >
          {LECTURE_STATUS_FILTER_LIST.filter(
            (filter) => filter !== LECTURE_STATUS_FILTER.EXPIRED,
          ).map((filter) => (
            <DropdownItem key={filter} value={filter}>
              {LECTURE_STATUS_FILTER_LABEL[filter]}
            </DropdownItem>
          ))}
        </Dropdown>
      )
    }

    return (
      <div css={S.StatusFilterGroup}>
        {LECTURE_STATUS_FILTER_LIST.filter(
          (filter) => filter !== LECTURE_STATUS_FILTER.EXPIRED,
        ).map((filter) => (
          <button
            key={filter}
            css={S.StatusFilterButton}
            data-active={lectureStatusFilter === filter}
            onClick={() => onChangeLectureStatusFilter(filter)}
          >
            {LECTURE_STATUS_FILTER_LABEL[filter]}
          </button>
        ))}
      </div>
    )
  })()

  return (
    <header css={S.Header}>
      <div className="left">{leftContent}</div>
      {commonRepo.studentAppSetting?.lectureService && isAllLecturesEnabled && (
        <div className="right">
          <span className="switch-label">
            {isMobile ? `전체 ${tJosa('강의')}` : `전체 ${tJosa('강의')} 보기`}
          </span>
          <Switch size="s" checked={isShowAllLectures} onCheckedChange={onToggleShowAllLectures} />
        </div>
      )}
    </header>
  )
}

export default observer(LectureGroupHeader)

const S = {
  Header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;

    .curriculum-list {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .left {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .right {
      display: flex;
      align-items: center;
      gap: 10px;

      .switch-label {
        ${typo.body01}
        color: var(--Colors-Gray-900);
      }
    }
  `,
  StatusFilterGroup: css`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    min-width: 280px;
    width: 384px;
  `,
  StatusFilterButton: css`
    flex: 1 0 0;
    max-width: 90px;
    padding: 10px 16px;
    border-radius: var(--Radius-200);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 12px;
    line-height: 18px;
    cursor: pointer;
    background: var(--White);
    border: 1px solid var(--Colors-Gray-300);
    color: var(--Colors-Gray-900);

    &[data-active='true'] {
      background: var(--Colors-Blue-600);
      color: var(--White);
      border: none;
    }
  `,
}
